import "../../util/mobile-table.css";
import {
  notification,
  Button,
  Table,
  Space,
} from "antd";
import Title from "antd/lib/typography/Title";
import { useContext, useEffect, useState} from "react";
import { ConfiguratorContext, S3UrlMapContext} from "../../context";
import { QuoteComment, CommentTopic } from "../../api/models"
import _ from "lodash";
import { useIntl } from "react-intl";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {CommentRequestOptions} from "../../api";
import QuoteCommentCard, {EditQuoteCommentCard} from "../QuoteCommentCard";
import { useQuoteContext } from "../../contexts/QuoteContext";
import BmMentions from "../BmMentions";
import Utils from "../../util/util";
import useUsers from "../../swr/useUsers";
import useQuoteComments from "../../swr/useQuoteComments";
import useQuoteCommentDocumentUrls from "../../swr/useQuoteCommentDocumentUrls";


export const getDefaultCommentTopics = (isAdminOrEngineering?:boolean) => {
  return ( isAdminOrEngineering )
    ? [CommentTopic.UserComment, CommentTopic.SystemActivity, CommentTopic.VfdReview ]
    : [CommentTopic.UserComment, CommentTopic.SystemActivity ];
 }

const QuoteCommentList = (props: {
  topics?: CommentTopic[]
  tags?:string[] | undefined
  hideSearch?:boolean
}) => {

  const configurator = useContext(ConfiguratorContext);
  const isAdminOrEngineering = configurator.isAdmin() || configurator.isEngineering();

  const DefaultTopics = props.topics || getDefaultCommentTopics(isAdminOrEngineering);

  const intl = useIntl();
  const [editCommentId, setEditCommentId] = useState<number | undefined>();
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showAddComment, setShowAddComment] = useState<boolean>(false);
  const { quote, adminView } = useQuoteContext();
  const [commentOptions, setCommentOptions] = useState<CommentRequestOptions | undefined>({
    showHidden: adminView,
    topic: DefaultTopics,
  });
  const [search, setSearch] = useState<string | undefined>();

  const userLst = useUsers().data;

  useEffect(() => {
    setSearch(props.tags?.join(" "));
  }, [props.tags] );

  const quoteComments = useQuoteComments({
    quoteId: quote?.quoteId,
    options: commentOptions,
  })

  const s3UrlMap = useQuoteCommentDocumentUrls({
    quoteId: quote?.quoteId,
    options: commentOptions,
  })

  useEffect(() => {
    const quoteId = quote?.quoteId;
    if ( quoteId?.length ) {
      Promise.all( quoteComments.data?.map( c => {
        updateQuoteCommentLastViewed(c.id);
      } ) || [] );
    }
  }, [quote?.quoteId, commentOptions?.showHidden, props.topics, quoteComments.isLoading] );

  const updateQuoteCommentLastViewed = async (commentId:number) : Promise<QuoteComment | undefined> => {

    try {
      const resp = await configurator.api.updateCommentLastViewed(commentId);
      return resp.data;
    } catch (e:any) {
      const errorMsg = intl.formatMessage({ id: e.message });
      notification.error( { message: "Failed to update quote comment last viewed. " + errorMsg });
    }
    return;
  }

  const handleShowAddComment = () => {
    setEditCommentId(undefined);
    setShowAddComment(true);
  }

  const handleEditComment = (commentId:number) => {
    setEditCommentId(commentId);
    setShowAddComment(false);
  }


  const handleAddedComment = (comment:QuoteComment) => {
    quoteComments.mutate();
    setShowAddComment(false);
  }

  const handleEditedComment = (comment:QuoteComment) => {
    quoteComments.mutate();
    setEditCommentId(undefined);
  }

  const handleToggleComments = () => {
    const topic = _.isEqual( commentOptions?.topic, [ CommentTopic.UserComment ] ) ? DefaultTopics : [CommentTopic.UserComment];
    setCommentOptions( {...commentOptions, topic } );
  }

  const handleToggleActivities = () => {
    const topic = _.isEqual( commentOptions?.topic, [ CommentTopic.SystemActivity ] ) ? DefaultTopics : [CommentTopic.SystemActivity];
    setCommentOptions( {...commentOptions, topic } );
  }

  const handleToggleVfd = () => {
    const topic = _.isEqual( commentOptions?.topic, [ CommentTopic.VfdReview ] ) ? DefaultTopics : [CommentTopic.VfdReview];
    setCommentOptions( {...commentOptions, topic } );
  }

  const isAddCommentsVisible = ( commentOptions?.topic?.includes( CommentTopic.UserComment ) && !showAddComment );
  const isActivitiesVisible = (commentOptions?.topic?.includes(CommentTopic.SystemActivity) && !showAddComment);
  const isVfdVisible = commentOptions?.topic?.includes(CommentTopic.VfdReview);
  const activitiesTitleStyle = { fontWeight: isActivitiesVisible  ? 700 : 400 }
  const commentTitleStyle = { fontWeight: isAddCommentsVisible ? 700 : 400 }
  const vfdTitleStyle = { fontWeight: isVfdVisible ? 700 : 400 }

  const datasource = quoteComments.data
    ?.filter( c => {
      if ( !search ) return true;
      const searchFilter = Utils.splitByWhitespacePreservingQuotes(search);
      const searchTxt = [c.text].concat(c.tags?.flat()).join(" ");
      return Utils.searchValue( searchFilter, searchTxt );
    })
    .sort((a,b) => dayjs(a.createdAt).isAfter(b.createdAt) ? -1 : 1);
  const pageSize = 7;
  const pagination = ((datasource?.length || 0) > pageSize) ? { pageSize } : false; 

  const topics = DefaultTopics
  .map( t => {
    if( t === CommentTopic.UserComment ) {
      return <Button type="text" ghost onClick={handleToggleComments}><Title level={5} style={commentTitleStyle}>Comments</Title></Button> 
    }
    if( t === CommentTopic.SystemActivity ) {
      return <Button type="text" ghost onClick={handleToggleActivities}><Title level={5} style={activitiesTitleStyle}>Activities</Title></Button>
    }
    if( t === CommentTopic.VfdReview ) {
      return <Button type="text" ghost onClick={handleToggleVfd}><Title level={5} style={vfdTitleStyle}>VFD</Title></Button> 
    }
    return <></>;
  })
  .reduce((acc,v) => {
    if ( v ) {
      return <>{acc}|{v}</>
    }
    return v;
  });

  return <>
    <S3UrlMapContext.Provider value={{s3UrlMap: s3UrlMap.data}}>

      <Space direction="vertical" style={{width: "100%"}}>

        <div style={{display: "flex", alignItems: "baseline", gap: ".2rem"}}>
          <div>{topics}</div>
          <div>
            <Button icon={<SearchOutlined />} shape="circle" type="primary" size="small"
                    onClick={() => setShowSearch(!showSearch)}
            />
          </div>
          <div>
          {!showAddComment && isAddCommentsVisible  &&
              <Button icon={<PlusOutlined />} shape="circle" type="primary" size="small"
                      onClick={handleShowAddComment}
              />}
          </div>
        </div>

        {(!props.hideSearch && showSearch && !showAddComment) &&
            <div style={{margin: "0px 0px 0px 8px", padding: "0px 8px 0px 0px"}} >
              <BmMentions
                  allowClear
                  rows={1}
                  value={search}
                  placeholder="Search Comments"
                  onChange={(txt) => setSearch( txt)}
                  mentions={{
                    '@': userLst?.map( u => ({value: _.upperFirst(_.camelCase( u.name)), label: u.name })),
                    '#': props.tags?.map( t => ({value: _.upperFirst(_.camelCase( t )), label: t }))
                  }}
              />
            </div> }


        {showAddComment &&
      <EditQuoteCommentCard 
        quoteId={quote?.quoteId}
        comment={{
          topic: CommentTopic.UserComment
        }}
        reset={showAddComment}
        onCancel={() => setShowAddComment(false)}
        onSave={handleAddedComment}
      />}

      <Table
        className="mobile-table"
        size="small"
        bordered={false}
        rowKey="id"
        loading={quoteComments.isLoading}
        dataSource={datasource}
        pagination={pagination}
        columns={[{ render: (comment) => 
          (editCommentId === comment.id ) 
            ? <EditQuoteCommentCard comment={comment} 
              quoteId={quote?.quoteId}
              onCancel={() => setEditCommentId(undefined)}
              onSave={handleEditedComment}
            /> 
            : <QuoteCommentCard comment={comment} 
              onEdit={handleEditComment} 
            />
        }]}
      />
      </Space>

    </S3UrlMapContext.Provider>
  </>
}

export default QuoteCommentList;


